import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NewFilter from './NewFilter';
import '../assets/stylesheets/allinone.css';

const Allinone = () => {
  const apiKey = process.env.REACT_APP_TMDB_API_KEY;
  const gameApiKey = process.env.REACT_APP_RAWGIO_API_KEY;
  const serverURL = process.env.REACT_APP_API_URI;
  const navigate = useNavigate();

  const movieBaseURL = `https://api.themoviedb.org/3/discover/movie?api_key=${apiKey}`;
  const tvBaseURL = `https://api.themoviedb.org/3/discover/tv?api_key=${apiKey}`;
  const gameBaseURL = `https://api.rawg.io/api`;

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [tempFilteredProducts, setTempFilteredProducts] = useState([]);
  const [gameMap, setGameMap] = useState(new Map());
  const [movieMap, setMovieMap] = useState(new Map());
  const [genres, setGenres] = useState([]);
  const [genreURL, setGenreURL] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [releaseDateFromURL, setReleaseDateFromURL] = useState();
  const [releaseDateToURL, setReleaseDateToURL] = useState();
  const [languageURL, setLanguageURL] = useState();
  const [genresOpen, setGenresOpen] = useState(false);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [questions, setQuestions] = useState([]);
  const [ratingso, setRatingso] = useState([]);
  const [sortOrder, setSortOrder] = useState('ascending');
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [rawgGames, setRawgGames] = useState([]);
  const [isUsingFilter, setIsUsingFilter] = useState(false);
  const [filterResetSignal, setFilterResetSignal] = useState(0);

  const genresRef = useRef(null);

  // --- Fetch Categories ---
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${serverURL}/context/get-all-category`);
        setCategories(res.data.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCategories();
  }, [serverURL]);

  const handleDateFromChange = (e) => {
    setReleaseDateFromURL(e.target.value);
    setCurrentPage(1);
  };
  const handleDateToChange = (e) => {
    setReleaseDateToURL(e.target.value);
    setCurrentPage(1);
  };
  const handleLanguageChange = (e) => {
    setLanguageURL(e.target.value);
    setCurrentPage(1);
  };

  function handleGenreChange(e) {
    const genreId = e.target.value;
    if (e.target.checked) {
      setGenreURL((prev) => [...prev, genreId]);
    } else {
      setGenreURL((prev) => prev.filter((item) => item !== genreId));
    }
    setCurrentPage(1);
  }

  useEffect(() => {
    if (selectedCategory !== 'Movie' && selectedCategory !== 'Television')
      return;

    async function fetchData() {
      try {
        let api = selectedCategory === 'Movie' ? movieBaseURL : tvBaseURL;
        const releaseDateParam =
          selectedCategory === 'Movie'
            ? 'primary_release_date'
            : 'first_air_date';
        if (releaseDateFromURL) {
          api += `&${releaseDateParam}.gte=${releaseDateFromURL}`;
        }
        if (releaseDateToURL) {
          api += `&${releaseDateParam}.lte=${releaseDateToURL}`;
        }
        if (languageURL) {
          api += `&language=${languageURL}`;
        }
        if (genreURL.length > 0) {
          api += `&with_genres=${genreURL.toString()}`;
        }
        api += `&page=${currentPage}`;

        const response = await axios.get(api);

        if (response && response.data.results.length > 0) {
          setItems(response.data.results);

          setTotalPages(response.data['total_pages']);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }
    if (!isUsingFilter) {
      fetchData();
    } else {
      updateFilters();
    }
  }, [
    selectedCategory,
    releaseDateFromURL,
    releaseDateToURL,
    languageURL,
    genreURL,
    currentPage,
    isUsingFilter,
  ]);

  useEffect(() => {
    if (selectedCategory !== 'Movie' && selectedCategory !== 'Television')
      return;
    async function fetchGenreAndLanguage() {
      try {
        const mediaType = selectedCategory === 'Movie' ? 'movie' : 'tv';

        const responseLanguage = await axios.get(
          'https://api.themoviedb.org/3/configuration/languages?api_key=5885fe9dd784d8e0af439f0d75b6dda5',
        );

        const responseGenre = await axios.get(
          `https://api.themoviedb.org/3/genre/${mediaType}/list?api_key=5885fe9dd784d8e0af439f0d75b6dda5`,
        );

        if (responseGenre.data && responseGenre.data.genres) {
          setGenres(responseGenre.data.genres);
        }
        if (responseLanguage.data) {
          setLanguages(
            responseLanguage.data.sort((a, b) =>
              a.english_name.localeCompare(b.english_name),
            ),
          );
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }

    fetchGenreAndLanguage();
  }, [selectedCategory]);

  // --- Handle Click Outside for Genres Dropdown ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (genresRef.current && !genresRef.current.contains(event.target)) {
        setGenresOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setGenresOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleCategoryChange = (e) => {
    const title = e.target.value;
    const selectedCat = categories.find((c) => c.title === title);
    setSelectedCategory(title);
    setSelectedCategoryId(selectedCat?._id || '');
    // Reset filters
    setDateStart('');
    setDateEnd('');
    setGenreURL([]);
    setTagSearch('');
    setFilteredProducts([]);
    setTempFilteredProducts([]);
    setIsUsingFilter(false);
    setFilterResetSignal((prev) => prev + 1);
  };

  const handlePrevious = () => currentPage > 1 && setCurrentPage((p) => p - 1);
  const handleNext = () =>
    currentPage < totalPages && setCurrentPage((p) => p + 1);

  // --- Fetch Backend Products ---
  useEffect(() => {
    const fetchBackendProducts = async () => {
      if (!selectedCategoryId) return;
      try {
        const res = await axios.post(
          `${serverURL}/entertainment/get-all-ratings-for-user`,
          {
            catId: selectedCategoryId,
          },
        );
        const data = res.data;
        if (!data) return;

        const uniqueIds = new Set();
        const uniqueProducts = [];
        data.products.forEach((p) => {
          if (!uniqueIds.has(p.contentId)) {
            uniqueIds.add(p.contentId);
            uniqueProducts.push(p);
          }
        });

        setProducts(uniqueProducts);
        setFilteredProducts(uniqueProducts);
        setTempFilteredProducts(uniqueProducts);
        setRatingso(data.ratings || []);
        setQuestions(data.questions.slice(0, -1) || []);
        setTotalPages(Math.ceil(uniqueProducts.length / 50));
        setCurrentPage(1);
      } catch (err) {
        console.error(
          'Backend Fetch Error:',
          err.response?.data || err.message,
        );
      }
    };
    fetchBackendProducts();
  }, [selectedCategoryId, serverURL]);

  // --- Fetch RAWG Genres ---
  useEffect(() => {
    if (selectedCategory !== 'Video Games') return;
    const fetchGameGenres = async () => {
      try {
        const res = await axios.get(`${gameBaseURL}/genres?key=${gameApiKey}`);
        setGenres(res.data.results || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchGameGenres();
  }, [selectedCategory]);

  // --- Fetch RAWG Games (for default display) ---
  useEffect(() => {
    if (selectedCategory !== 'Video Games' || isUsingFilter) return;

    const fetchRawgGames = async () => {
      try {
        const pageSize = 32;
        const res = await axios.get(
          `${gameBaseURL}/games?key=${gameApiKey}&page_size=${pageSize}&page=${currentPage}`,
        );
        setRawgGames(res.data.results || []);
        const totalCount = res.data.count || 0;
        setTotalPages(Math.ceil(totalCount / pageSize));
      } catch (err) {
        console.error(err);
      }
    };

    fetchRawgGames();
  }, [selectedCategory, isUsingFilter, currentPage]);

  // --- Fetch TMDB Details ---
  useEffect(() => {
    if (
      (selectedCategory !== 'Movie' && selectedCategory !== 'Television') ||
      !isUsingFilter ||
      products.length === 0
    )
      return;

    const fetchMovieDetails = async () => {
      try {
        const ids = products.map((p) => p.contentId);

        const chunkSize = 50; // max safe batch
        const chunks = [];
        for (let i = 0; i < ids.length; i += chunkSize) {
          chunks.push(ids.slice(i, i + chunkSize));
        }

        let allResults = [];

        for (const chunk of chunks) {
          // Create requests for this batch
          const requests = chunk.map((id) => {
            const url =
              selectedCategory == 'Movie'
                ? `${movieBaseURL.substring(0, 28)}/movie/${id}?api_key=${apiKey}`
                : `${tvBaseURL.substring(0, 28)}/tv/${id}?api_key=${apiKey}`;
            return axios.get(url);
          });

          // Run the batch safely
          const results = await Promise.allSettled(requests);

          // Push only successful responses
          results.forEach((r) => {
            if (r.status === 'fulfilled') {
              allResults.push(r.value.data);
            }
          });

          // Delay between chunks to stay under 50 req/sec
          await new Promise((resolve) => setTimeout(resolve, 50));
        }

        // Map results by ID
        const newMap = new Map();
        allResults.forEach((movie) => newMap.set(+movie.id, movie));

        setMovieMap(newMap);
      } catch (err) {
        console.error('Unexpected error:', err);
      }
    };

    fetchMovieDetails();
  }, [products, selectedCategory, isUsingFilter]);

  // --- Fetch RAWG Game Details ---
  useEffect(() => {
    if (selectedCategory !== 'Video Games' || products.length === 0) return;

    const fetchGameDetails = async () => {
      try {
        const ids = products.map((p) => p.contentId);
        const chunks = [];
        const chunkSize = 20;
        for (let i = 0; i < ids.length; i += chunkSize) {
          chunks.push(ids.slice(i, i + chunkSize));
        }

        const responses = await Promise.all(
          chunks.map((chunk) =>
            axios.get(
              `${gameBaseURL}/games?key=${gameApiKey}&ids=${chunk.join(',')}`,
            ),
          ),
        );

        const allGames = responses.flatMap((r) => r.data.results || []);
        const newMap = new Map();
        allGames.forEach((g) => newMap.set(g.id, g));

        setGameMap(newMap);
      } catch (err) {
        console.error(err);
      }
    };

    fetchGameDetails();
  }, [products, selectedCategory]);

  // --- Auto-update filtered products ---
  useEffect(() => {
    if (selectedCategory !== 'Video Games' || !isUsingFilter) return;

    let filtered = products;

    if (dateStart)
      filtered = filtered.filter((p) => {
        const release = gameMap.get(parseInt(p.contentId))?.released;
        return release && Date.parse(release) >= Date.parse(dateStart);
      });
    if (dateEnd)
      filtered = filtered.filter((p) => {
        const release = gameMap.get(parseInt(p.contentId))?.released;
        return release && Date.parse(release) <= Date.parse(dateEnd);
      });
    if (genreURL.length > 0)
      filtered = filtered.filter((p) => {
        const gameGenres = gameMap.get(parseInt(p.contentId))?.genres || [];
        return gameGenres.some((g) => genreURL.includes(g.slug));
      });
    if (tagSearch) {
      const tagsArray = tagSearch
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      filtered = filtered.filter((p) => {
        const tags = gameMap.get(parseInt(p.contentId))?.tags || [];
        return tagsArray.every((tag) =>
          tags.some((t) => t.slug.toLowerCase().includes(tag)),
        );
      });
    }

    setFilteredProducts(filtered);
    setTempFilteredProducts(filtered);
    setCurrentPage(1);
  }, [
    dateStart,
    dateEnd,
    genreURL,
    tagSearch,
    products,
    gameMap,
    selectedCategory,
  ]);

  useEffect(() => {
    if (selectedCategory !== 'Video Games' || isUsingFilter) return;

    const fetchFilteredRawgGames = async () => {
      try {
        const pageSize = 32;
        const params = new URLSearchParams({
          key: gameApiKey,
          page: currentPage,
          page_size: pageSize,
        });

        // Date filters
        if (dateStart) {
          const currDate = new Date();
          params.append(
            'dates',
            `${dateStart},${dateEnd || currDate.toISOString().substring(0, 10)}`,
          );
        }
        if (dateEnd && !dateStart)
          params.append('dates', `1970-01-01,${dateEnd}`);

        // Genres
        if (genreURL.length > 0) params.append('genres', genreURL.join(','));

        // Tags
        if (tagSearch)
          params.append(
            'tags',
            tagSearch
              .split(',')
              .map((t) => t.trim())
              .join(','),
          );

        const res = await axios.get(
          `${gameBaseURL}/games?${params.toString()}`,
        );
        setRawgGames(res.data.results || []);
        const totalCount = res.data.count || 0;
        setTotalPages(Math.ceil(totalCount / pageSize));
      } catch (err) {
        console.error(err);
      }
    };

    fetchFilteredRawgGames();
  }, [
    selectedCategory,
    currentPage,
    dateStart,
    dateEnd,
    genreURL,
    tagSearch,
    isUsingFilter,
  ]);

  const updateFilters = () => {
    var list = tempFilteredProducts;

    if (releaseDateFromURL) {
      const releaseDateFrom = new Date(releaseDateFromURL);
      list = list.filter((p) => {
        const releaseDate = new Date(movieMap.get(+p.contentId)?.release_date);
        return releaseDate > releaseDateFrom;
      });
    }

    if (releaseDateToURL) {
      const releaseDateTo = new Date(releaseDateToURL);
      list = list.filter((p) => {
        const releaseDate = new Date(movieMap.get(+p.contentId)?.release_date);
        return releaseDate < releaseDateTo;
      });
    }

    if (languageURL) {
      console.log(languageURL);
      list = list.filter(
        (p) => movieMap.get(+p.contentId)?.original_language === languageURL,
      );
    }

    list = list.filter((p) => {
      var isIncluded = true;
      genreURL.forEach((g, i) => {
        if (isIncluded) {
          var hasGenre = false;
          if (!hasGenre) {
            movieMap.get(+p.contentId)?.genres.forEach((g2, i2) => {
              if (+g2?.id === +g) {
                hasGenre = true;
              }
            });
            if (!hasGenre) {
              isIncluded = false;
            }
          }
        }
      });
      return isIncluded;
    });
    setFilteredProducts(list);
  };

  // Switch view based on filter state
  const handleFilteredResults = (results, isActive) => {
    setIsUsingFilter(isActive);

    if (isActive && results.length > 0) {
      const filtered = results
        .map((id) => products.find((p) => p.contentId === id))
        .filter(Boolean);
      setFilteredProducts(filtered);
      setTempFilteredProducts(filtered);
    } else {
      setFilteredProducts([]); // will trigger RAWG display
      setTempFilteredProducts([]);
    }
    if (selectedCategory === 'Movie' || selectedCategory === 'TV') {
      updateFilters();
    }
  };

  const handleSortOrderChange = (order) => setSortOrder(order);

  let displayProducts = [];
  if (selectedCategory === 'Video Games') {
    displayProducts = isUsingFilter
      ? filteredProducts
      : rawgGames.map((g) => ({
          contentId: g.id,
          contentName: g.name,
          contentDescription: g.slug,
          contentImage: g.background_image,
        }));
  } else if (
    selectedCategory === 'Movie' ||
    selectedCategory === 'Television'
  ) {
    displayProducts = isUsingFilter ? filteredProducts : items;
  } else {
    displayProducts = filteredProducts;
  }

  return (
    <div className="max-w-screen-xl mx-auto mt-5 mb-6 lg:h-[calc(90vh-80px)]">
      <div className="flex flex-col md:flex-row h-full">
        {/* Left Sidebar */}
        <aside className="bg-gray-900 p-3 mb-4 lg:py-4 lg:px-2 mt-4 w-full md:w-1/4 rounded-xl overflow-y-auto h-full">
          <div className="flex py-2 px-6 flex-wrap mt-2">
            <h3 className="text-lg font-semibold text-secondary">Category</h3>
            <select
              className="p-2 border rounded w-full"
              value={selectedCategory}
              onChange={handleCategoryChange}
            >
              <option value="">-- Select a category --</option>
              {categories.map((c) => (
                <option key={c._id} value={c.title}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>

          <div className="px-3 mt-4">
            {questions.length > 0 ? (
              <NewFilter
                ratingso={ratingso}
                questions={questions}
                onFilteredResults={handleFilteredResults}
                sortOrder={sortOrder}
                onSortOrderChange={handleSortOrderChange}
                resetSignal={filterResetSignal}
              />
            ) : (
              <p className="text-gray-500 mt-4">
                No questions available for this class.
              </p>
            )}
          </div>
        </aside>

        {/* Right Content */}
        <div className="flex-1 px-5 mt-4 overflow-y-auto h-full">
          {/* Video Game Top Search */}
          {selectedCategory === 'Video Games' && (
            <div className="bg-gray-900 border border-gray-800 text-gray-200 p-4 rounded-xl mb-6 shadow-md">
              <div className="text-sm font-semibold text-secondary mb-3 tracking-wide">
                <h3>Filters</h3>
              </div>
              <div className="flex flex-wrap gap-4 items-end">
                {/* Date Filters */}
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Released after:
                  </label>
                  <input
                    type="date"
                    value={dateStart}
                    onChange={(e) => {
                      setDateStart(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-44 p-2 bg-white text-gray-800 border border-gray-300 rounded-lg
                              focus:ring-2 focus:ring-secondary focus:outline-none"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Released before:
                  </label>
                  <input
                    type="date"
                    value={dateEnd}
                    onChange={(e) => {
                      setDateEnd(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-44 p-2 bg-white text-gray-800 border border-gray-300 rounded-lg
                              focus:ring-2 focus:ring-secondary focus:outline-none"
                  />
                </div>

                {/* Genres Dropdown */}
                <div className="flex flex-col relative" ref={genresRef}>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Genres
                  </label>
                  <button
                    className="w-44 bg-white text-gray-800 border border-gray-300 rounded-lg p-2 flex justify-between items-center hover:border-secondary transition-colors"
                    onClick={() => setGenresOpen(!genresOpen)}
                  >
                    <span>
                      {genreURL.length > 0
                        ? `Selected (${genreURL.length})`
                        : 'Select genres'}
                    </span>
                    <svg
                      className={`w-4 h-4 transition-transform ${genresOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {genresOpen && (
                    <div className="absolute z-20 w-44 bg-white text-gray-800 border border-gray-300 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-xl">
                      {genres.length === 0 ? (
                        <p className="p-3 text-gray-500 text-sm">
                          Loading genres...
                        </p>
                      ) : (
                        genres.map((g) => (
                          <label
                            key={g.id}
                            className="flex items-center p-2 hover:bg-gray-100 cursor-pointer text-sm"
                          >
                            <input
                              type="checkbox"
                              value={g.slug}
                              checked={genreURL.includes(g.slug)}
                              onChange={(e) => {
                                const value = e.target.value;
                                setGenreURL((prev) =>
                                  prev.includes(value)
                                    ? prev.filter((v) => v !== value)
                                    : [...prev, value],
                                );
                                setCurrentPage(1);
                              }}
                              className="mr-2 accent-secondary"
                            />
                            {g.name}
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Tags Input */}
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Tags
                  </label>
                  <input
                    type="text"
                    placeholder="comma separated"
                    value={tagSearch}
                    onChange={(e) => {
                      setTagSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-52 p-2 bg-white text-gray-800 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:outline-none placeholder-gray-500"
                  />
                </div>

                {/* Clear Button */}
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-400 mb-1 opacity-0">
                    Clear
                  </label>
                  <button
                    className="bg-secondary hover:bg-secondary/80 text-white px-4 py-2 rounded-lg transition-all"
                    onClick={() => {
                      setDateStart('');
                      setDateEnd('');
                      setGenreURL([]);
                      setTagSearch('');
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Movies and TV Top Search */}
          {(selectedCategory === 'Movie' ||
            selectedCategory === 'Television') && (
            <div className="bg-gray-900 border border-gray-800 text-gray-200 p-4 rounded-xl mb-6 shadow-md">
              <div className="text-sm font-semibold text-secondary mb-3 tracking-wide">
                <h3>Filters</h3>
              </div>
              <div className="flex flex-wrap gap-4 items-end">
                {/* Date Filters */}
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Released after:
                  </label>
                  <input
                    type="date"
                    value={releaseDateFromURL}
                    onChange={handleDateFromChange}
                    className="w-44 p-2 bg-white text-gray-800 border border-gray-300 rounded-lg
                              focus:ring-2 focus:ring-secondary focus:outline-none"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Released before:
                  </label>
                  <input
                    type="date"
                    value={releaseDateToURL}
                    onChange={handleDateToChange}
                    className="w-44 p-2 bg-white text-gray-800 border border-gray-300 rounded-lg
                              focus:ring-2 focus:ring-secondary focus:outline-none"
                  />
                </div>

                <div className="flex flex-col">
                  <h3 className="block text-sm font-medium text-gray-400 mb-1">
                    Language
                  </h3>
                  <select
                    name="language"
                    id="language"
                    value={languageURL}
                    onChange={handleLanguageChange}
                  >
                    <option className="mr-2 accent-secondary" value="">
                      Select language
                    </option>
                    {languages.map((language, index) => (
                      <option value={language['iso_639_1']} key={index}>
                        {language['english_name']}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Genres Dropdown */}
                <div className="flex flex-col relative" ref={genresRef}>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Genres
                  </label>
                  <button
                    className="w-44 bg-white text-gray-800 border border-gray-300 rounded-lg p-2 flex justify-between items-center hover:border-secondary transition-colors"
                    onClick={() => setGenresOpen(!genresOpen)}
                  >
                    <span>
                      {genreURL.length > 0
                        ? `Selected (${genreURL.length})`
                        : 'Select genres'}
                    </span>
                    <svg
                      className={`w-4 h-4 transition-transform ${genresOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {genresOpen && (
                    <div className="absolute z-20 w-44 bg-white text-gray-800 border border-gray-300 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-xl">
                      {genres.length === 0 ? (
                        <p className="p-3 text-gray-500 text-sm">
                          Loading genres...
                        </p>
                      ) : (
                        genres.map((genre) => (
                          <label
                            key={genre.id}
                            className="flex items-center p-2 hover:bg-gray-100 cursor-pointer text-sm"
                          >
                            <input
                              type="checkbox"
                              id={genre.id}
                              name={genre.name}
                              value={genre.id}
                              className="mr-2 accent-secondary"
                              onChange={handleGenreChange}
                              checked={genreURL.includes(String(genre.id))}
                            />
                            {genre.name}
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Clear Button */}
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-400 mb-1 opacity-0">
                    Clear
                  </label>
                  <button
                    className="bg-secondary hover:bg-secondary/80 text-white px-4 py-2 rounded-lg transition-all"
                    onClick={() => {
                      setReleaseDateFromURL('');
                      setReleaseDateToURL('');
                      setLanguageURL('');
                      setGenreURL([]);
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Display Products */}
          {selectedCategory === 'Video Games' && (
            <div className="grid lg:grid-cols-4 grid-cols-2 gap-4">
              {displayProducts.length === 0 ? (
                <p className="col-span-full text-center text-gray-500">
                  No products available.
                </p>
              ) : (
                displayProducts.map((p) => {
                  const game = gameMap.get(parseInt(p.contentId));
                  return (
                    <div
                      key={p.contentId}
                      className="game-card relative rounded-xl shadow-lg overflow-hidden cursor-pointer hover:scale-105 transform transition-transform"
                      onClick={() => navigate(`/rate/game/${p.contentId}`)}
                    >
                      <img
                        src={
                          isUsingFilter
                            ? game?.background_image
                            : p.contentImage
                        }
                        alt={p.contentName}
                        className="w-full h-48 object-cover"
                      />

                      {/* Tooltip overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-center px-2">
                          {p.contentName}
                        </span>
                      </div>
                      <div className="p-2">
                        <h3 className="text-lg font-bold text-gray-800 mb-1">
                          {p.contentName}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-3">
                          {p.contentDescription}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Movies and TV Shows */}
          {(selectedCategory === 'Movie' ||
            selectedCategory === 'Television') && (
            <div className="grid lg:grid-cols-4 grid-cols-2 gap-3">
              {displayProducts.length > 0 ? (
                displayProducts.map((m) => {
                  const movie = isUsingFilter ? movieMap.get(+m.contentId) : m;
                  return (
                    <a
                      key={+m.contentId}
                      href={`/rate/movie/${isUsingFilter ? m.contentId : movie.id}`}
                      className="rounded-3xl shadow-xl overflow-hidden transform transition-transform hover:scale-105 hover:shadow-2xl"
                    >
                      {movie?.poster_path ? (
                        <img
                          src={`https://image.tmdb.org/t/p/w500/${movie.poster_path}`}
                          alt={movie.title}
                          className="w-full h-72 object-cover rounded-xl"
                        />
                      ) : (
                        <div className="w-full h-72 bg-gray-200 flex items-center justify-center text-gray-500 rounded-xl">
                          Loading...
                        </div>
                      )}
                    </a>
                  );
                })
              ) : selectedCategory === 'Movie' ? (
                <div className="col-span-full text-white justify-center w-full flex">
                  No movies available.
                </div>
              ) : (
                <div className="col-span-full text-white justify-center w-full flex">
                  No TV shows available.
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && !isUsingFilter && (
            <div className="flex justify-center my-5 gap-4">
              <button
                className={`px-4 py-2 rounded-full shadow-md ${currentPage === 1 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-secondary text-primary'}`}
                onClick={handlePrevious}
                disabled={currentPage === 1}
              >
                &lt; Previous
              </button>
              <span className="mx-4 text-lg font-semibold flex justify-center items-center bg-gray-100 text-gray-700 px-4 py-1 rounded-full shadow-md">
                {currentPage} / {totalPages}
              </span>
              <button
                className={`px-4 py-2 rounded-full shadow-md ${currentPage === totalPages ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-secondary text-primary'}`}
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                Next &gt;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Allinone;
